import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { WorkoutService } from '../../../lib/workoutService';
import { TrainerAppLayout } from '../shell/TrainerAppLayout';
import { TrainerWorkoutDetailEditView } from './TrainerWorkoutDetailEditView';
import { createEmptyWorkoutDraft } from './utils/trainerWorkoutEditUtils';
import { TrainerConfirmDialog } from './overlays/TrainerConfirmDialog';
import type { WorkoutSummary, WorkoutWithBlocks } from '../../../types/dataModel';
import './TrainerWorkoutDetailPage.css';
import './TrainerWorkoutDetailPage.edit.css';
import './TrainerWorkoutDetailPage.create.css';

const EMPTY_DRAFT = createEmptyWorkoutDraft();

function checkNameExists(
  workouts: WorkoutSummary[],
  name: string,
  excludeWorkoutId?: string,
): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  return workouts.some(
    (w) =>
      w.name.trim().toLowerCase() === trimmed.toLowerCase() && w.id !== excludeWorkoutId,
  );
}

export const TrainerWorkoutCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = location.state as {
    returnTo?: string;
    returnMode?: 'create' | 'view' | 'edit';
    reopenAssignTargets?: number[];
    assignOnCreate?: { memberId: string; date: string };
  } | null;

  const handleReturnToPlan = useCallback(() => {
    if (!returnState?.returnTo) return;
    // Cancel create: return without assigning (member detail may reopen library later).
    navigate(returnState.returnTo, {
      state: returnState.assignOnCreate
        ? { scrollToCalendar: true }
        : {
            returnMode: returnState.returnMode,
            reopenAssignTargets: returnState.reopenAssignTargets,
            restorePlanDraft: true,
          },
    });
  }, [navigate, returnState]);
  const [workouts, setWorkouts] = useState<WorkoutSummary[]>([]);
  const [dirty, setDirty] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    WorkoutService.getWorkoutsSummary()
      .then((rows) => {
        if (!cancelled) setWorkouts(rows);
      })
      .catch((err) => console.error('Load workouts for name check:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const handleBackToLibrary = useCallback(() => {
    try {
      localStorage.setItem('trainer_dashboard_active_section', 'library');
      localStorage.setItem('trainer_dashboard_active_tab', 'workouts');
    } catch {
      // ignore
    }
    navigate('/trainer-dashboard');
  }, [navigate]);

  const requestLeave = useCallback(
    (navigateAway: () => void) => {
      if (dirty) {
        setPendingNavigation(() => navigateAway);
        setLeaveConfirmOpen(true);
        return;
      }
      navigateAway();
    },
    [dirty],
  );

  const handleDiscardNavigate = () => {
    if (returnState?.returnTo) {
      if (dirty) {
        setPendingNavigation(() => handleReturnToPlan);
        setLeaveConfirmOpen(true);
        return;
      }
      handleReturnToPlan();
      return;
    }
    requestLeave(handleBackToLibrary);
  };

  const handleExitCreate = () => {
    if (returnState?.returnTo) {
      handleDiscardNavigate();
      return;
    }
    handleBackToLibrary();
  };

  const confirmLeave = () => {
    setLeaveConfirmOpen(false);
    setDirty(false);
    const go = pendingNavigation;
    setPendingNavigation(null);
    go?.();
  };

  const cancelLeave = () => {
    setLeaveConfirmOpen(false);
    setPendingNavigation(null);
  };

  const handleCreated = async (result: WorkoutWithBlocks) => {
    setDirty(false);
    if (returnState?.returnTo) {
      if (returnState.assignOnCreate) {
        navigate(returnState.returnTo, {
          state: {
            assignCreatedWorkout: {
              workoutId: result.id,
              date: returnState.assignOnCreate.date,
              name: result.name,
            },
            scrollToCalendar: true,
          },
        });
        return;
      }
      navigate(returnState.returnTo, {
        state: {
          returnMode: returnState.returnMode,
          reopenAssignTargets: returnState.reopenAssignTargets,
          restorePlanDraft: true,
        },
      });
      return;
    }
    try {
      localStorage.setItem('trainer_dashboard_active_section', 'library');
      localStorage.setItem('trainer_dashboard_active_tab', 'workouts');
    } catch {
      // ignore
    }
    navigate('/trainer-dashboard', {
      state: { workoutCreated: true, workoutName: result.name },
    });
  };

  const handleRailSelect = (section: 'members' | 'library' | 'chat' | 'control') => {
    requestLeave(() => {
      try {
        localStorage.setItem('trainer_dashboard_active_section', section);
      } catch {
        // ignore
      }
      navigate('/trainer-dashboard');
    });
  };

  const breadcrumb = (
    <nav className="twd-breadcrumb" aria-label="Breadcrumb">
      <Link
        to="/trainer-dashboard"
        onClick={(e) => {
          e.preventDefault();
          requestLeave(handleBackToLibrary);
        }}
      >
        Library
      </Link>
      <span className="twd-bc-sep" aria-hidden="true">
        /
      </span>
      <Link
        to="/trainer-dashboard"
        onClick={(e) => {
          e.preventDefault();
          requestLeave(handleBackToLibrary);
        }}
      >
        Workouts
      </Link>
      <span className="twd-bc-sep" aria-hidden="true">
        /
      </span>
      <span className="twd-bc-current">New workout</span>
    </nav>
  );

  return (
    <TrainerAppLayout
      activeSection="library"
      onSectionSelect={handleRailSelect}
      shellClassName="twd-page twd-page-with-rail twd-page--edit twd-page--create"
      mainClassName="twd-main"
    >
      <div className="twd-topbar">{breadcrumb}</div>

      <TrainerWorkoutDetailEditView
        mode="create"
        workout={EMPTY_DRAFT}
        onSaved={handleCreated}
        onCreated={handleCreated}
        onExitEditMode={handleExitCreate}
        onDiscardNavigate={handleDiscardNavigate}
        checkWorkoutNameExists={(name) => checkNameExists(workouts, name)}
        onDirtyChange={setDirty}
      />

      {leaveConfirmOpen ? (
        <TrainerConfirmDialog
          open
          title="Leave without creating?"
          message="Your workout draft will be lost."
          confirmLabel="Leave"
          onCancel={cancelLeave}
          onConfirm={confirmLeave}
        />
      ) : null}
    </TrainerAppLayout>
  );
};

export default TrainerWorkoutCreatePage;
